import { useMemo, useState } from 'react'
import { callFunction } from '@/shared/firebase/callFunction'
import { previewMerge } from '@/shared/email/mergeFields'
import type { Flow } from '../../../../functions-webhook/src/automation/flows'
import { variantsFor } from '../../../../functions-webhook/src/emailVariants'
import {
  mergeFor,
  profilesFor,
  simulate,
  when,
} from '../../../../functions-webhook/src/automation/simulate'
import type { Newsletter } from '../repository/newsletterRepository'
import { Icon } from './BlockIcon'
import { FlowStage, type BlockLook } from './FlowDetail'
import { layoutFlow, tagsIn } from './flowText'

/**
 * Test mode — artboard 4, 13 Oct 2026.
 *
 * The flow played by the real engine (`simulate.ts`) on a fake clock against
 * made-up profiles. Nothing reads or changes a real contact, and nothing is
 * sent unless somebody presses Send to me — which goes to their own address
 * only, decided by the server from the signed-in account.
 */
export function TestMode({ flow, template }: { flow: Flow; template: Newsletter | undefined }) {
  const profiles = useMemo(() => profilesFor(flow), [flow])
  const [profileId, setProfileId] = useState(profiles[0]!.id)
  const profile = profiles.find((p) => p.id === profileId) ?? profiles[0]!
  const sim = useMemo(() => simulate(flow, profile), [flow, profile])
  const [step, setStep] = useState(1)
  const last = sim.moments.length - 1
  const at = Math.min(step, last)
  const m = sim.moments[at]
  const layout = useMemo(() => layoutFlow(flow), [flow])
  const [showFull, setShowFull] = useState(false)
  const [sending, setSending] = useState(false)
  const [note, setNote] = useState<string | null>(null)

  const pick = (id: string) => {
    setProfileId(id)
    setStep(1)
    setNote(null)
  }

  if (!m) {
    return (
      <div className="flowd__main">
        <p className="flowd__callout">This flow cannot be simulated yet.</p>
      </div>
    )
  }

  const look = (id: string): BlockLook => {
    if (id === m.here) return { state: 'here', note: m.notes[id] ?? '' }
    if (m.visited.includes(id)) return { state: 'passed', note: m.notes[id] ?? '' }
    if (m.done) return { state: 'skipped', note: '' }
    return { state: 'idle', note: '' }
  }
  const path = ['trigger', ...m.visited.filter((x) => x !== 'trigger')]
  const lit = (from: string, to: string) => path.includes(from) && path.includes(to)

  // The email, as this profile would get it — only once it has been sent.
  const got = m.sentAtMs !== null && sim.sentTo !== null
  const merge = sim.sentTo ? mergeFor(flow, sim.sentTo) : null
  const tags = template ? tagsIn(`${template.subject} ${template.html}`) : []
  const fill = (text: string) => previewMerge(text, merge?.values ?? {})

  const sendToMe = async () => {
    if (!template || !merge) return
    setSending(true)
    setNote(null)
    try {
      const out = await callFunction<{ to: string }>('crmAutomationTestSend', {
        method: 'POST',
        body: { flow: flow.id, templateId: template.id, values: merge.values, segment: merge.segment },
      })
      setNote(`Sent to ${out.to}.`)
    } catch (e) {
      setNote(`Could not send: ${e instanceof Error ? e.message : String(e)}`)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="flowd__body">
      <aside className="flowd__palette flowd__testside" aria-label="Simulation">
        <fieldset className="flowd__fs">
          <legend>Simulated profile</legend>
          {profiles.map((p) => (
            <label key={p.id} className={`flowd__radio${p.id === profile.id ? ' flowd__radio--on' : ''}`}>
              <input type="radio" name="sim-profile" checked={p.id === profile.id} onChange={() => pick(p.id)} />
              <span>
                <strong>{p.label}</strong>
                <span className="flowd__muted flowd__small">{p.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div className="flowd__fs">
          <span className="flowd__legend">Move time forward</span>
          <ol className="flowd__steps">
            {sim.moments.map((x, i) => (
              <li key={i} className={i === at ? 'flowd__step--on' : i < at ? 'flowd__step--past' : ''}>
                <button type="button" onClick={() => setStep(i)}>
                  <span className="flowd__small">{when(x.atMs)}</span>
                  <span>{x.label}</span>
                </button>
              </li>
            ))}
          </ol>
          <div className="flowd__actions">
            <button type="button" className="btn btn--ghost" disabled={at === 0} onClick={() => setStep(at - 1)}>
              Back
            </button>
            <button type="button" className="btn btn--primary" disabled={at >= last} onClick={() => setStep(at + 1)}>
              Move forward
            </button>
          </div>
        </div>
        <p className="flowd__hint">
          A fake clock plays the flow against made-up profiles, with the same engine that sends the
          real emails. Nothing reads or changes real contacts, and nothing is sent unless you press
          Send to me.
        </p>
      </aside>

      <section className="flowd__flow">
        <div className="flowd__toolbar">
          <div className="flowd__legendrow" aria-label="Legend">
            <span className="flowd__key flowd__key--passed">Passed</span>
            <span className="flowd__key flowd__key--here">Here now</span>
            <span className="flowd__key flowd__key--skipped">Not taken</span>
          </div>
          <span className="flowd__muted flowd__small">{when(m.atMs)} · Europe/Oslo</span>
        </div>
        <FlowStage
          flow={flow}
          template={template}
          layout={layout}
          scale={typeof window !== 'undefined' && window.innerWidth < 700 ? 0.5 : 1}
          line={() => ''}
          look={look}
          litEdge={lit}
        />
      </section>

      <aside className="flowd__panel" aria-label="Simulation results">
        <div className="flowd__phead">
          <h3>What happened</h3>
        </div>
        <ol className="flowd__log">
          {m.log.map((l, i) => (
            <li key={i}>
              <span className="flowd__small flowd__muted">{when(l.atMs)}</span>
              <span>{l.text}</span>
            </li>
          ))}
        </ol>

        <div className="flowd__stats">
          <h3 className="flowd__h4">Email this profile gets</h3>
          {!got && (
            <p className="flowd__muted">
              {m.done
                ? `No email. ${m.exit ?? 'The flow ended'}.`
                : 'No email yet. Move time forward.'}
            </p>
          )}
          {got && !template && (
            <p className="flowd__muted">The flow sends here, but no email is chosen yet. Choose one on the canvas.</p>
          )}
          {got && template && (
            <>
              <div className="flowd__field flowd__field--stack">
                <span className="flowd__flabel">Subject</span>
                <span className="flowd__fvalue">{fill(template.subject)}</span>
              </div>
              {tags.map((t) => {
                const v = merge?.values[t.tag]
                const fb = t.shown.includes('|') ? t.shown.split('|')[1] : ''
                return (
                  <div className="flowd__stat" key={t.tag}>
                    <span>{t.tag}</span>
                    <strong>{v ? v : fb ? `${fb} (fallback)` : '(empty)'}</strong>
                  </div>
                )
              })}
              <div className="flowd__stat">
                <span>Sent at</span>
                <strong>
                  {when(m.sentAtMs!)}
                  {profile.contact.timezone ? '' : ' Europe/Oslo'}
                </strong>
              </div>
              <div className="flowd__actions">
                <button type="button" className="btn btn--primary" disabled={sending} onClick={() => void sendToMe()}>
                  <Icon name="mail" size={16} /> {sending ? 'Sending…' : 'Send to me'}
                </button>
                <button type="button" className="btn btn--ghost" onClick={() => setShowFull((v) => !v)}>
                  {showFull ? 'Hide the full email' : 'Open the full email'}
                </button>
              </div>
              <p className="flowd__muted flowd__small">
                Send to me sends this email, tags filled from the profile, to your own address only.
              </p>
              {note && <p className="flowd__callout">{note}</p>}
              {showFull && (
                <iframe
                  className="flowd__frame"
                  title="The full email"
                  sandbox=""
                  srcDoc={fill(variantsFor(template.html)([merge!.segment]))}
                />
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  )
}
